import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useMemo } from 'react';
import { format } from 'date-fns';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import { Tooltip, Typography, Grid, Box } from '@mui/material';
import AffiliateLeadStatusComponent from 'app/shared-components/AffiliateLeadStatusComponent';
import { type AffiliateLead } from './types';
import { getProcessorLabel } from 'src/app/consts/processorOptions';

interface Props {
  onIsApprovedChange: (lead: AffiliateLead) => void;
}

const useAffiliateLeadsColDef = ({ onIsApprovedChange }: Props): GridColDef<AffiliateLead>[] => {
  return useMemo(
    () => [
      {
        field: 'contactDate',
        headerName: 'Lead Request',
        type: 'date',
        sortable: true,
        minWidth: 150,
        valueGetter: (_, row: AffiliateLead) => {
          if (!row.contactDate) return null;
          const utcDate = new Date(row.contactDate);
          const timezoneOffset = utcDate.getTimezoneOffset();
          return new Date(utcDate.getTime() + timezoneOffset * 60000);
        },
        renderCell: (params: GridRenderCellParams) => (
          <span>{params.value ? format(params.value, 'MMM, dd, yyyy') : ''}</span>
        ),
      },
      { field: 'firstName', headerName: 'First Name', sortable: true, minWidth: 130 },
      { field: 'lastName', headerName: 'Last Name', sortable: true, minWidth: 130 },
      {
        field: 'email',
        headerName: 'Email',
        sortable: true,
        minWidth: 220,
        renderCell: (params: GridRenderCellParams<AffiliateLead>) => (
          <a href={`mailto:${params.value}`}>{params.value}</a>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        sortable: true,
        renderHeader: () => (
          <>
            Status
            <Tooltip
              title={
                <Grid container direction="column" spacing={1}>
                  <Grid item>
                    <Typography variant="subtitle1" className="text-white">
                      Lead Status Explained:
                    </Typography>
                  </Grid>
                  {[
                    [
                      <FuseSvgIcon>material-outline:edit</FuseSvgIcon>,
                      "Draft: The lead is in 'Draft' status and has not yet been sent to the North processor. While in this status, the lead can be edited or deleted by the admin.",
                    ],
                    [
                      <FuseSvgIcon>material-outline:task</FuseSvgIcon>,
                      "Uploaded: The lead has the status 'Uploaded', meaning it was successfully transferred to North.",
                    ],
                    [
                      <FuseSvgIcon>material-outline:toggle_on</FuseSvgIcon>,
                      'Approved / Unapproved: Use the switch to manually update the North Feedback status for any uploaded lead. A blue switch indicates Approved, while a gray switch means Unapproved. This status can be changed by the admin at any time.',
                    ],
                    [
                      <FuseSvgIcon>material-outline:hourglass_empty</FuseSvgIcon>,
                      'Marked For Sending / Processing: The lead is marked for sending or is currently being processed and is waiting to be transferred to North.',
                    ],
                    [
                      <FuseSvgIcon color="white">material-outline:error_outline</FuseSvgIcon>,
                      'Failed: Upload failed. If the lead seems to be correct, contact development team.',
                    ],
                  ].map(([iconElement, text], idx) => (
                    <Grid item key={idx}>
                      <Box display="flex" alignItems="flex-start" gap={1.5}>
                        {iconElement}
                        <Typography className="text-white">{text}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              }
            >
              <FuseSvgIcon className="ml-4 cursor-pointer" size={20} color="action">
                material-outline:info
              </FuseSvgIcon>
            </Tooltip>
          </>
        ),
        renderCell: (params: GridRenderCellParams<AffiliateLead>) => {
          const lead = params.row;
          return (
            <Box display="flex" alignItems="center" height="100%">
              {lead.status === 'MarkedForUpload' || lead.status === 'Processing' ? (
                <FuseSvgIcon color="action">material-outline:hourglass_empty</FuseSvgIcon>
              ) : (
                <AffiliateLeadStatusComponent
                  status={lead.status}
                  isApproved={lead.isApproved}
                  onIsApprovedChange={() => onIsApprovedChange(lead)}
                />
              )}
            </Box>
          );
        },
      },
      {
        field: 'uploadTarget',
        headerName: 'Target',
        sortable: true,
        minWidth: 160,
        renderCell: (params: GridRenderCellParams<AffiliateLead>) => (
          <span>{params.value ? getProcessorLabel(params.value as string) : ''}</span>
        ),
      },
      {
        field: 'phoneNumber',
        headerName: 'Phone Number',
        sortable: true,
        minWidth: 160,
        renderCell: (params: GridRenderCellParams<AffiliateLead>) => {
          const cleaned = ('' + params.value).replace(/\D/g, '');
          const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
          return match ? `(${match[1]}) ${match[2]}-${match[3]}` : (params.value as string);
        },
      },
      {
        field: 'address',
        headerName: 'Address',
        sortable: true,
        minWidth: 220,
        maxWidth: 250,
        renderCell: (params: GridRenderCellParams<AffiliateLead>) => (
          <div
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'normal',
              lineHeight: '1.5',
            }}
          >
            {params.row.address}
          </div>
        ),
      },
      {
        field: 'dba',
        headerName: 'DBA',
        sortable: true,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams<AffiliateLead>) => (
          <div
            style={{
              whiteSpace: 'normal',
              lineHeight: '1.5',
              maxHeight: '48px',
              overflow: 'hidden',
            }}
          >
            {params.value}
          </div>
        ),
      },
      {
        field: 'createdAt',
        headerName: 'Uploaded at',
        type: 'date',
        sortable: true,
        minWidth: 160,
        valueGetter: (_: unknown, row: AffiliateLead): Date | null =>
          row.createdAt ? new Date(row.createdAt) : null,
        renderCell: (params: GridRenderCellParams) => (
          <span>{params.value ? format(params.value, 'MMM, dd, yyyy') : ''}</span>
        ),
      },
      {
        field: 'uploadedAt',
        headerName: 'Sent at',
        type: 'date',
        sortable: true,
        minWidth: 160,
        valueGetter: (_: unknown, row: AffiliateLead): Date | null =>
          row.uploadedAt ? new Date(row.uploadedAt) : null,
        renderCell: (params: GridRenderCellParams) => (
          <span>{params.value ? format(params.value, 'MMM, dd, yyyy') : ''}</span>
        ),
      },
      { field: 'city', headerName: 'City', sortable: true, minWidth: 130 },
      { field: 'state', headerName: 'State', sortable: true, minWidth: 130 },
      { field: 'zipCode', headerName: 'Zip Code', sortable: true, minWidth: 130 },
    ],
    [onIsApprovedChange],
  );
};

export default useAffiliateLeadsColDef;
